import { classifyFurniture } from "helpers/batchFurniture.helper"
import { hasPriceForSeries } from "helpers/modelNumberCatalogue.helper"
import type { TFurniture, TPageElevationData } from "types/elevationReferences.type"
import type { TModelNumberSummary } from "types/modelNumbers.type"

export const MODEL_NUMBER_MISMATCH_LABEL = "Model number has no price for the selected series"
export const FURNITURE_MISMATCH_MESSAGE = "Object's model number has no price for the selected series"
export const EREF_MISMATCH_MESSAGE =
	"Elevation reference contains objects whose model number has no price for the selected series"
export const PAGE_MISMATCH_MESSAGE =
	"Page contains elevation references with objects whose model number has no price for the selected series"
export const UNASSIGNED_MISMATCH_MESSAGE = "Contains objects whose model number has no price for the selected series"

/** The eref sidebar's REQ-250 count line. */
export const formatMismatchCount = (count: number) =>
	count === 1
		? "1 completed cabinet has a model number with no price for the selected series"
		: `${count} completed cabinets have a model number with no price for the selected series`

/** REQ-249 only blocks COMPLETING (`in_quotation`) a cabinet, so only those can drift into a mismatch. */
export const isCompletedCabinet = (item: TFurniture) =>
	item.status === "in_quotation" && item.selected_model_number != null && classifyFurniture(item) === "cabinet"

/** Model numbers whose prices are needed to check every completed cabinet in the quotation. */
export const collectCompletedCabinetModelNumberIds = (pages: readonly TPageElevationData[]): number[] => {
	const ids = new Set<number>()
	for (const page of pages) {
		for (const item of page.furniture) {
			if (isCompletedCabinet(item) && item.selected_model_number) ids.add(item.selected_model_number.id)
		}
	}
	return [...ids]
}

/**
 * Ids of the completed cabinets whose model number has no price for their effective series — the
 * connected eref's override, else the quotation's series. An unknown series or a model number the
 * price lookup did not return is not flagged.
 */
export const findMismatchedFurnitureIds = (
	pages: readonly TPageElevationData[],
	quotationSeriesId: number | null,
	modelNumbers: readonly TModelNumberSummary[],
): Set<number> => {
	const byId = new Map(modelNumbers.map((model) => [model.id, model]))
	const mismatched = new Set<number>()
	for (const page of pages) {
		const erefsById = new Map(page.erefs.map((eref) => [eref.id, eref]))
		for (const item of page.furniture) {
			if (!isCompletedCabinet(item) || !item.selected_model_number) continue
			const model = byId.get(item.selected_model_number.id)
			if (!model) continue
			const eref = item.eref_id == null ? undefined : erefsById.get(item.eref_id)
			const seriesId = eref?.estimation_config?.series_override_id ?? quotationSeriesId
			if (!hasPriceForSeries(model, seriesId)) mismatched.add(item.id)
		}
	}
	return mismatched
}

export const hasMismatch = (furniture: readonly TFurniture[], mismatchedIds: ReadonlySet<number>) =>
	furniture.some((item) => mismatchedIds.has(item.id))

export const countMismatches = (furniture: readonly TFurniture[], mismatchedIds: ReadonlySet<number>) =>
	furniture.filter((item) => mismatchedIds.has(item.id)).length
